import { useEffect } from 'react'
import { useInView } from 'react-intersection-observer'
import clsx from 'clsx'
import Link from 'next/link'

import { CustomerTypes } from 'modules/customer'
import ResponsiveImage from 'shared/ui/ResponsiveImage/ResponsiveImage'
import { tabletBreakPoint } from 'shared/ui/utils/breakpoints'

import { BannerTypes } from '../../../submodules/banners/api'

import styles from './BannerSlide.module.scss'

// Same switch point as the `tablet` SCSS mixin (`$bp-tablet`, 1025px) that toggles desktopImage/mobileImage
const MOBILE_MEDIA = `(max-width: ${tabletBreakPoint + 1}px)`
const DESKTOP_MEDIA = `not all and ${MOBILE_MEDIA}`

interface BannerSlideProps extends React.HTMLAttributes<HTMLDivElement> {
  banner: BannerTypes
  isBlurred: boolean
  idx: number
  src: string
  alt: string
  handleBlurredBannerClick: () => void
  handlePromoClick: (banner: BannerTypes, idx: number) => void
  user: CustomerTypes
  handleViewPromotion: (banner: BannerTypes, idx: number) => void
  isConfirmAgeRequired: boolean
  isViewed: boolean
  isActive: boolean
}
export default function BannerSlide({
  banner,
  isBlurred,
  isConfirmAgeRequired,
  idx,
  alt,
  handleBlurredBannerClick,
  handlePromoClick,
  handleViewPromotion,
  isViewed,
  isActive,
  className,
  ...props
}: BannerSlideProps) {
  const [ref, inView] = useInView({
    triggerOnce: true,
    initialInView: false,
    threshold: 0.5
  })

  useEffect(() => {
    if (inView && !isViewed) {
      handleViewPromotion(banner, idx)
    }
  }, [inView, isViewed])

  return (
    <div
      ref={ref}
      key={banner.id}
      onClick={() =>
        isBlurred ? handleBlurredBannerClick() : handlePromoClick(banner, idx)
      }
      {...props}
      className={clsx(styles.slideWrapper, className)}
    >
      {isBlurred && (
        <div className={styles.blurredInfo}>
          {isConfirmAgeRequired
            ? 'Potwierdź datę urodzenia'
            : 'Nie spełniasz wymagań wiekowych'}
        </div>
      )}
      <div
        className={clsx(styles.blurWrapper, { [styles.blurred]: isBlurred })}
      >
        <Link
          href={banner?.destinationUrl || ''}
          id={'banner_slider_' + (idx + 1).toString()}
          aria-label={banner.title}
          tabIndex={isActive ? 0 : -1}
          aria-hidden={!isActive}
          className={styles.link}
        >
          <div className={styles.bannerWrapper} data-title={banner.title}>
            <ResponsiveImage
              src={banner.webImage}
              alt={alt}
              priority={idx === 0}
              visibleMedia={DESKTOP_MEDIA}
              hiddenMedia={MOBILE_MEDIA}
              className={clsx(styles.image, styles.desktopImage)}
            />
            <ResponsiveImage
              src={banner.mobileImage}
              alt={alt}
              priority={idx === 0}
              visibleMedia={MOBILE_MEDIA}
              hiddenMedia={DESKTOP_MEDIA}
              className={clsx(styles.image, styles.mobileImage)}
            />
          </div>
        </Link>
      </div>
    </div>
  )
}
